import React, { useState, useEffect, useCallback, useRef } from "react";
import { Row, Col, Card, Button, message, Alert, Tooltip, Select, Skeleton, Segmented } from "antd";
import {
  DeploymentUnitOutlined,
  FileTextOutlined,
  HeartOutlined,
} from "@ant-design/icons";
import "./DashboardPage.css";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useNavigate } from "react-router-dom";
import { dashboardData as dashboardService } from "../services/dashboardData";
import { logsService } from "../services/logsApi";
import { accountService } from "../services/accountService";
import { createLogger } from "../utils/logger";

import { AreaChart } from "recharts";
import AreaChartExample from "./AreaChart";
import SmoothMountainChart from "./AreaChart";
import TinyHorizontalBarChart from "./HorizontalChart";
import DataTypeChartRecharts from "./HorizontalChart";
import DataTypeChart from "./HorizontalChart";
import ThinBarChart from "./BarChart";
import DualLineChart from "./AreaChart";
import CustomDualLineChart from "./AreaChart";

import DonutChartWithRightLabels from "./DonutChart";
import { logsAnalysisService } from "../services/logsAnalysisService";
import { databricksService } from "../services/databricksService";
import { healthService } from "../services/healthService";
import { pipelineDiscoveryService } from "../services/pipelineDiscoveryService";

import CostBarChart from "./AreaChart";

import FiringAlertsList from "../components/FiringAlertList";
//new design components
import TopServicesHorizontalBars from "../components/TopServicesHorizontalBars";
import ResourceTrendChart from "../components/ResourceTrendChart";
import LogVolumeTimeline from "../components/LogVolumeTimeline";
import DataUsageChart from "../components/DataUsageChart";

import awsLogoIcon from "../assets/icons/aws-logo.png";
import databricksLogoIcon from "../assets/icons/databricks-logo.png";
import regionsLogoIcon from "../assets/icons/regions-logo.svg";

const DATABRICKS_ACCOUNTS_REFRESH_INTERVAL_MS = 30_000; // 30 seconds
const DASHBOARD_DATA_POLL_INTERVAL_MS = 30_000;
const DASHBOARD_VIEW_MODE_STORAGE_KEY = "clairai.homeDashboardMode";
const logger = createLogger('DashboardPage');

const kpiBrandIcon = (src, alt, className) => (
  <img src={src} alt={alt} className={className} />
);

const DashboardPage = () => {
  const { user, dashboardData, setDashboardData, accounts: authAccounts } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [servicePieData, setServicePieData] = useState([]);
  const [servicePieLoading, setServicePieLoading] = useState(false);
  const [logAccountOptions, setLogAccountOptions] = useState([]);
  const [selectedLogAccount, setSelectedLogAccount] = useState("");
  const [databricksAccountsCount, setDatabricksAccountsCount] = useState(0);
  const [databricksLoading, setDatabricksLoading] = useState(true);
  const [totalLogCount, setTotalLogCount] = useState(null);
  const [totalLogCountLoading, setTotalLogCountLoading] = useState(false);
  const [pipelineCount, setPipelineCount] = useState(null);
  const [pipelineCountLoading, setPipelineCountLoading] = useState(false);
  const [pipelineCountRefreshing, setPipelineCountRefreshing] = useState(false);
  const [pipelineCountAccountId, setPipelineCountAccountId] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [healthStatus, setHealthStatus] = useState(null);
  const [dashboardMode, setDashboardMode] = useState(() => {
    if (typeof window === "undefined") return "normal";
    const storedMode = window.localStorage.getItem(DASHBOARD_VIEW_MODE_STORAGE_KEY);
    return storedMode === "detailed" ? "detailed" : "normal";
  });
  const [insightsReady, setInsightsReady] = useState(false);
  const dashboardContentRef = useRef(null);

  const buildOptionsFromAuthAccounts = (items = [], preferredClientId) => {
    if (!Array.isArray(items) || items.length === 0) return [];

    const scoped = preferredClientId
      ? items.filter((item) => item?.clientId === preferredClientId)
      : [];
    const source = scoped.length > 0 ? scoped : items;
    const multiClient = new Set(source.map((item) => item?.clientId).filter(Boolean)).size > 1;

    return source
      .filter((item) => item?.accountId)
      .map((item) => ({
        value: item.accountId,
        label: multiClient
          ? `${item.aws_account_name || "AWS Account"} (${item.accountId}) - ${item.clientId || "unknown"}`
          : `${item.aws_account_name || "AWS Account"} (${item.accountId})`,
      }));
  };

  const buildOptionsFromAccountsMap = (accountsMap = {}, preferredClientId) => {
    if (!accountsMap || typeof accountsMap !== "object") return [];

    const scopedMap =
      preferredClientId &&
      accountsMap[preferredClientId] &&
      Object.keys(accountsMap[preferredClientId] || {}).length > 0
        ? { [preferredClientId]: accountsMap[preferredClientId] }
        : accountsMap;

    const clientIds = Object.keys(scopedMap);
    const multiClient = clientIds.length > 1;
    const options = [];

    for (const clientId of clientIds) {
      const clientAccounts = scopedMap[clientId] || {};
      for (const [id, details] of Object.entries(clientAccounts)) {
        options.push({
          value: id,
          label: multiClient
            ? `${details?.aws_account_name || "AWS Account"} (${id}) - ${clientId}`
            : `${details?.aws_account_name || "AWS Account"} (${id})`,
        });
      }
    }

    return options;
  };

  const parseVolumeInMb = (raw) => {
    if (raw === null || raw === undefined) return 0;
    const value = String(raw).trim();
    const numericMatch = value.match(/(\d+\.?\d*)/);
    if (!numericMatch) return 0;
    const amount = parseFloat(numericMatch[1]);
    if (!Number.isFinite(amount)) return 0;

    if (/gb/i.test(value)) return amount * 1024;
    if (/kb/i.test(value)) return amount / 1024;
    return amount;
  };

  const formatVolumeFromMb = (mb) => {
    if (!Number.isFinite(mb) || mb <= 0) return "0 MB";
    if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
    return `${mb.toFixed(2)} MB`;
  };

  // const mapServiceAnalysisResponse = (apiData) => {
  //   const results = Array.isArray(apiData?.volume_by_service) ? apiData.volume_by_service : [];

  //   return results
  //     .map((item) => {
  //       const name = item?.metric?.service_name;
  //       if (!name) return null;
  //       const normalized = String(name).trim().toLowerCase();
  //       if (!normalized || normalized === "unknown" || normalized === "unknown_service") return null;

  //       const valueMb = parseVolumeInMb(item?.value?.[1]);
  //       return {
  //         name,
  //         value: valueMb,
  //         formatted: formatVolumeFromMb(valueMb),
  //       };
  //     })
  //     .filter(Boolean);
  // };
  const mapServiceAnalysisResponse = (apiData) => {
    const serviceResults = Array.isArray(apiData?.volume_by_service)
      ? apiData.volume_by_service
      : [];

    const bucketResults = Array.isArray(apiData?.volume_by_bucket)
      ? apiData.volume_by_bucket
      : [];

    const logGroupResults = Array.isArray(apiData?.volume_by_log_group)
      ? apiData.volume_by_log_group
      : [];

    const uniqueMap = new Map();

    const addItem = (item, type) => {
      const metric = item?.metric || {};

      const name =
        metric.service_name ||
        metric.service ||
        metric.log_group ||
        metric.cloudwatch_log_group ||
        (metric.s3_bucket ? `s3://${metric.s3_bucket}` : null) ||
        (metric.bucket ? `s3://${metric.bucket}` : null);

      if (!name) return;

      const normalized = String(name).trim().toLowerCase();

      if (
        normalized === "unknown" ||
        normalized === "unknown_service" ||
        normalized === "error"
      ) {
        return;
      }

      // prevent duplicates
      if (uniqueMap.has(normalized)) {
        return;
      }

      const valueMb = parseVolumeInMb(item?.value?.[1]);

      uniqueMap.set(normalized, {
        name,
        value: valueMb,
        formatted: formatVolumeFromMb(valueMb),
        type,
      });
    };

    // order matters
    bucketResults.forEach((x) => addItem(x, "bucket"));
    serviceResults.forEach((x) => addItem(x, "service"));
    logGroupResults.forEach((x) => addItem(x, "log_group"));

    return Array.from(uniqueMap.values());
  };
  // Fetch Databricks accounts count from API
  const fetchDatabricksAccountsCount = useCallback(async () => {
    setDatabricksLoading(true);
    try {
      const accounts = await databricksService.getAccounts();
      const count = accounts && Array.isArray(accounts) ? accounts.length : 0;
      setDatabricksAccountsCount(count);
    } catch (error) {
      logger.error('Error fetching Databricks accounts count:', error);
      const databricksAccounts = databricksService.getFromLocalStorage();
      const count = Object.keys(databricksAccounts).length;
      setDatabricksAccountsCount(count);
    } finally {
      setDatabricksLoading(false);
    }
  }, []);
  
  // Fetch Databricks accounts count on mount and set up refresh interval
  useEffect(() => {
    fetchDatabricksAccountsCount();
    
    // Refresh periodically to catch new accounts
    const interval = setInterval(() => {
      fetchDatabricksAccountsCount();
    }, DATABRICKS_ACCOUNTS_REFRESH_INTERVAL_MS);
    
    // Listen for storage events (when accounts are added/removed from localStorage as fallback)
    const handleStorageChange = () => {
      fetchDatabricksAccountsCount();
    };
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom event when accounts are updated
    window.addEventListener('databricksAccountsUpdated', handleStorageChange);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('databricksAccountsUpdated', handleStorageChange);
    };
  }, [fetchDatabricksAccountsCount]);

  const fetchLogAccountOptions = useCallback(async () => {
    const preferredClientId = user?.client_id;

    try {
      let accountsList = buildOptionsFromAuthAccounts(authAccounts, preferredClientId);

      if (accountsList.length === 0 && preferredClientId) {
        try {
          const clientAccounts = await accountService.getAccountsByClient(preferredClientId);
          accountsList = Object.entries(clientAccounts || {}).map(([id, details]) => ({
            value: id,
            label: `${details?.aws_account_name || "AWS Account"} (${id})`,
          }));
        } catch (scopedError) {
          logger.debug("Client-scoped account fetch failed, falling back to all accounts", scopedError);
        }
      }

      if (accountsList.length === 0) {
        const allAccounts = await accountService.getAccounts();
        accountsList = buildOptionsFromAccountsMap(allAccounts, preferredClientId);
      }

      accountsList.sort((a, b) => a.label.localeCompare(b.label));
      const withAll = [{ value: "__all__", label: "All accounts" }, ...accountsList];
      setLogAccountOptions(withAll);

      setSelectedLogAccount((current) => {
        if (!current || !withAll.some((opt) => opt.value === current)) {
          // Default to first loaded account; keep "All accounts" as an explicit user choice.
          return accountsList[0]?.value || "__all__";
        }
        return current;
      });
    } catch (error) {
      logger.error("Error fetching log account options:", error);
      setLogAccountOptions([{ value: "__all__", label: "All accounts" }]);
      setSelectedLogAccount((current) => current || "__all__");
    }
  }, [authAccounts, user?.client_id]);

  useEffect(() => {
    fetchLogAccountOptions();
  }, [fetchLogAccountOptions]);

  // Total Pipelines card: live discovery count for the selected account.
  // Never fall back to dashboard num_jobs (configured services) — that caused 6 → 25 flash.
  useEffect(() => {
    const clientId = user?.client_id;
    const accountId =
      selectedLogAccount && selectedLogAccount !== "__all__"
        ? selectedLogAccount
        : logAccountOptions.find((o) => o.value !== "__all__")?.value;
    if (!clientId || !accountId) {
      setPipelineCountAccountId(null);
      setPipelineCount(null);
      setPipelineCountLoading(false);
      setPipelineCountRefreshing(false);
      return;
    }

    let cancelled = false;
    const cacheKey = `clairai_pipeline_count_${clientId}_${accountId}`;
    const cachedRaw =
      typeof window !== "undefined" ? window.sessionStorage.getItem(cacheKey) : null;
    const cachedCount =
      cachedRaw != null && cachedRaw !== "" && !Number.isNaN(Number(cachedRaw))
        ? Number(cachedRaw)
        : null;

    if (cachedCount != null) {
      setPipelineCount(cachedCount);
    } else {
      setPipelineCount(null);
    }
    setPipelineCountLoading(cachedCount == null);
    setPipelineCountRefreshing(cachedCount != null);

    pipelineDiscoveryService
      .discoverPipelines(clientId, accountId, {
        regions: null,
        autoOnboard: false,
      })
      .then((result) => {
        if (cancelled) return;
        const count = result?.summary?.total_pipelines ?? 0;
        setPipelineCount(count);
        setPipelineCountAccountId(accountId);
        if (typeof window !== "undefined") {
          window.sessionStorage.setItem(cacheKey, String(count));
        }
      })
      .catch((discoverErr) => {
        if (cancelled) return;
        logger.debug(
          "Live pipeline discovery failed on Home; falling back to status summary:",
          discoverErr,
        );
        return pipelineDiscoveryService
          .getPipelineStatus(clientId, accountId)
          .then((data) => {
            if (cancelled) return;
            setPipelineCount(data?.total ?? 0);
            setPipelineCountAccountId(accountId);
          })
          .catch((statusErr) => {
            if (cancelled) return;
            logger.debug("Pipeline status fallback failed:", statusErr);
            if (cachedCount == null) {
              setPipelineCount(0);
            }
            setPipelineCountAccountId(accountId);
          });
      })
      .finally(() => {
        if (!cancelled) {
          setPipelineCountLoading(false);
          setPipelineCountRefreshing(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user?.client_id, selectedLogAccount, logAccountOptions]);
  
  // Fetch service analysis for pie chart
  const fetchServicePieData = useCallback(async () => {
    const analysisType = "service";
    if (!selectedLogAccount) return;
    setServicePieLoading(true);
    setServicePieData([]);
    try {
      if (selectedLogAccount === "__all__") {
        const accountIds = logAccountOptions
          .filter((option) => option.value !== "__all__")
          .map((option) => option.value);

        if (accountIds.length === 0) return;

        logger.debug("Fetching service analysis for all accounts", { accounts: accountIds.length });
        const allResponses = await Promise.all(
          accountIds.map((accountId) =>
            logsAnalysisService
              .getServiceAnalysis(analysisType, "24h", accountId)
              .catch(() => null)
          )
        );

        const totalsByService = new Map();
        for (const apiData of allResponses) {
          const mapped = mapServiceAnalysisResponse(apiData);
          for (const service of mapped) {
            totalsByService.set(service.name, (totalsByService.get(service.name) || 0) + service.value);
          }
        }

        const mergedData = Array.from(totalsByService.entries())
          .map(([name, value]) => ({
            name,
            value,
            formatted: formatVolumeFromMb(value),
          }))
          .sort((a, b) => b.value - a.value);

        setServicePieData(mergedData);
        return;
      }

      logger.debug("Fetching service analysis for selected account", { accountId: selectedLogAccount });
      // const apiData = await logsAnalysisService.getServiceAnalysis(
      //   analysisType,
      //   "24h",
      //   selectedLogAccount
      // );
      // const mappedData = mapServiceAnalysisResponse(apiData).sort((a, b) => b.value - a.value);
      // setServicePieData(mappedData);
      const [
        serviceData,
        bucketData,
        logGroupData
      ] = await Promise.all([
        logsAnalysisService.getServiceAnalysis(
          "service",
          "24h",
          selectedLogAccount
        ),

        logsAnalysisService.getServiceAnalysis(
          "bucket",
          "24h",
          selectedLogAccount
        ),

        logsAnalysisService.getServiceAnalysis(
          "log_group",
          "24h",
          selectedLogAccount
        )
      ]);

      const mergedApiData = {
        volume_by_service: serviceData?.volume_by_service || [],
        volume_by_bucket: bucketData?.volume_by_bucket || [],
        volume_by_log_group: logGroupData?.volume_by_log_group || [],
      };

      const mappedData = mapServiceAnalysisResponse(mergedApiData)
        .sort((a, b) => b.value - a.value);

      setServicePieData(mappedData);
    } catch (e) {
      logger.error("Error fetching top services data:", e);
      setServicePieData([]);
    } finally {
      setServicePieLoading(false);
    }
  }, [selectedLogAccount, logAccountOptions]);

  useEffect(() => {
    if (!selectedLogAccount) return;
    if (dashboardMode !== "detailed" || !insightsReady) return;
    fetchServicePieData();
  }, [fetchServicePieData, selectedLogAccount, dashboardMode, insightsReady]);

  // Fetch and count all logs (AWS + Databricks)
  const fetchTotalLogCount = useCallback(async () => {
    if (!user?.client_id) {
      setTotalLogCountLoading(false);
      return;
    }
    // Keep existing/fallback count visible while we recalculate detailed totals.
    setTotalLogCountLoading(totalLogCount === null);
    try {
      let awsLogCount = 0;
      let databricksLogCount = 0;

      // Fetch AWS logs from all accounts
      try {
        const allAccounts = await accountService.getAccounts();
        const accountIds = [];
        
        // Extract account IDs from all clients
        for (const clientId in allAccounts) {
          accountIds.push(...Object.keys(allAccounts[clientId] || {}));
        }
        
        // Fetch logs for each AWS account
        const awsLogPromises = accountIds.map(async (accountId) => {
          try {
            const logs = await logsService.getOnboardedLogs(accountId, user.client_id);
            if (logs && logs.length > 0) {
              // Count all log selectors across all log entries
              return logs.reduce((count, logEntry) => {
                return count + (logEntry.log_selector?.length || 0);
              }, 0);
            }
            return 0;
          } catch (error) {
            logger.debug(`Error fetching logs for account ${accountId}:`, error);
            return 0;
          }
        });

        const awsLogCounts = await Promise.all(awsLogPromises);
        awsLogCount = awsLogCounts.reduce((sum, count) => sum + count, 0);
      } catch (error) {
        logger.error('Error fetching AWS logs count:', error);
      }

      // Fetch Databricks logs
      try {
        const databricksLogsResponse = await logsService.getAllDatabricksLogs();
        if (databricksLogsResponse && databricksLogsResponse.workspaces) {
          databricksLogCount = databricksLogsResponse.workspaces.reduce((count, workspace) => {
            let workspaceLogCount = 0;
            if (workspace.cluster_log_path) workspaceLogCount++;
            if (workspace.audit_log_path) workspaceLogCount++;
            return count + workspaceLogCount;
          }, 0);
        }
      } catch (error) {
        logger.error('Error fetching Databricks logs count:', error);
      }

      setTotalLogCount(awsLogCount + databricksLogCount);
    } catch (error) {
      logger.error('Error fetching total log count:', error);
      setTotalLogCount(0);
    } finally {
      setTotalLogCountLoading(false);
    }
  }, [user?.client_id, totalLogCount]);

  useEffect(() => {
    fetchTotalLogCount();
  }, [fetchTotalLogCount]);

  // Fetch health status
  const fetchHealthStatus = useCallback(async () => {
    try {
      const health = await healthService.getHealthStatus();
      setHealthStatus(health);
    } catch (error) {
      logger.error('Error fetching health status:', error);
      // Set unhealthy status if fetch fails
      setHealthStatus({
        status: 'unhealthy',
        error: error?.message || 'Health check unavailable'
      });
    }
  }, []);

  useEffect(() => {
    fetchHealthStatus();
    // Refresh health status every 30 seconds
    const healthInterval = setInterval(fetchHealthStatus, 30000);
    return () => clearInterval(healthInterval);
  }, [fetchHealthStatus]);

  const fetchDashboard = useCallback(async (opts = {}) => {
    const silent = Boolean(opts.silent);
    if (!user) return;
    if (!silent) {
      setDashboardLoading(true);
      setFetchError(null);
    }
    try {
      const data = await dashboardService.getAllAccountsDashboardData();
      setDashboardData({
        accounts: data.num_accounts,
        regions: data.num_regions,
        jobs: data.num_jobs,
        log_groups: data.num_log_groups,
      });
      setFetchError(null);
    } catch (error) {
      logger.error("Error fetching dashboard data:", error);
      if (!silent) {
        setFetchError(error?.message || "Failed to load dashboard data");
      }
    } finally {
      if (!silent) {
        setDashboardLoading(false);
      }
    }
  }, [user, setDashboardData]);

  useEffect(() => {
    if (!user) return;
    fetchDashboard({ silent: false });
    const poll = setInterval(() => {
      fetchDashboard({ silent: true });
    }, DASHBOARD_DATA_POLL_INTERVAL_MS);
    return () => clearInterval(poll);
  }, [user, fetchDashboard]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(DASHBOARD_VIEW_MODE_STORAGE_KEY, dashboardMode);
  }, [dashboardMode]);

  useEffect(() => {
    if (dashboardMode !== "detailed") {
      setInsightsReady(false);
      return;
    }
    const timer = window.setTimeout(() => {
      setInsightsReady(true);
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [dashboardMode]);

  const { accounts, regions, jobs, log_groups } = dashboardData;

  // No need to filter 'Unknown' services here anymore
  const filteredServicePieData = servicePieData;
  
  // Check if all services have 0 MB volume
  const hasNonZeroData = filteredServicePieData.some(item => item.value > 0);

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Get health status color
  const getHealthColor = () => {
    if (!healthStatus) return '#94a3b8'; // gray - checking
    if (healthStatus.status === 'healthy') return '#16a34a'; // green
    if (healthStatus.status === 'unhealthy') return '#dc2626'; // red
    return '#f59e0b'; // amber - degraded/unknown
  };

  const healthTooltipTitle = healthStatus ? (
    <div style={{ maxWidth: 300 }}>
      <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>
        System Health: {healthStatus.status === 'healthy' ? 'Healthy' : 'Unhealthy'}
      </div>
      {healthStatus.version && (
        <div style={{ fontSize: 12, marginBottom: 4 }}>Version: {healthStatus.version}</div>
      )}
      {healthStatus.environment && (
        <div style={{ fontSize: 12, marginBottom: 4 }}>Environment: {healthStatus.environment}</div>
      )}
      {healthStatus.uptime_seconds && (
        <div style={{ fontSize: 12, marginBottom: 8 }}>
          Uptime: {Math.floor(healthStatus.uptime_seconds / 3600)}h {Math.floor((healthStatus.uptime_seconds % 3600) / 60)}m
        </div>
      )}
      {healthStatus.checks && Object.keys(healthStatus.checks).length > 0 && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
          <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Checks:</div>
          {Object.entries(healthStatus.checks).map(([checkName, checkResult]) => (
            <div key={checkName} style={{ fontSize: 11, marginBottom: 2 }}>
              <span style={{ color: checkResult.status === 'healthy' ? '#4ade80' : checkResult.status === 'unhealthy' ? '#f87171' : '#94a3b8' }}>
                {checkName}: {checkResult.status}
              </span>
              {checkResult.response_time_ms && (
                <span style={{ opacity: 0.7, marginLeft: 4 }}>
                  ({checkResult.response_time_ms.toFixed(0)}ms)
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  ) : (
    'Checking health status...'
  );

  const healthKpiShort =
    !healthStatus ? '…' : healthStatus.status === 'healthy' ? 'OK' : healthStatus.status === 'unhealthy' ? 'Issue' : 'Warn';

  const kpiValueSkeleton = (
    <Skeleton.Input active size="small" className="dashboard-home__kpi-skeleton" aria-hidden />
  );

  const normalKpiColProps = dashboardMode === "normal"
    ? { xs: 24, sm: 12, lg: 12 }
    : { flex: "1 1 0", style: { minWidth: 0 } };

  return (
    <div>
      <div
        className={`dashboard-home ${isDark ? 'dashboard-home--dark' : 'dashboard-home--light'} ${
          dashboardMode === "normal" ? "dashboard-home--normal" : "dashboard-home--detailed"
        }`}
      >
          <header className="dashboard-home__sticky-bar">
            <div className="dashboard-home__header">
              <div className="dashboard-home__header-main">
                <h1 className="dashboard-home__title">
                  Home
                </h1>
                <p className="dashboard-home__subtitle">
                  Accounts, pipelines, and alert status at a glance
                </p>
              </div>
              <div className="dashboard-home__actions">
                <div className="dashboard-home__mode-toggle-wrap">
                  <span className="dashboard-home__mode-toggle-label">View</span>
                  <Segmented
                    value={dashboardMode}
                    onChange={setDashboardMode}
                    options={[
                      { label: "Normal", value: "normal" },
                      { label: "Detailed", value: "detailed" },
                    ]}
                    size="middle"
                    className="dashboard-home__mode-toggle"
                    aria-label="Home dashboard view mode"
                  />
                </div>
                {user && (
                  <span className="dashboard-home__greeting">
                    {getGreeting()}, {user.profile?.name || user.profile?.preferred_username || user.username || user.email || 'User'}
                  </span>
                )}
              </div>
            </div>
          </header>
          {fetchError && (
            <Alert
              type="error"
              message="Dashboard data unavailable"
              description={fetchError}
              action={
                <Button size="small" danger onClick={() => fetchDashboard({ silent: false })}>
                  Retry
                </Button>
              }
              closable
              onClose={() => setFetchError(null)}
              className="dashboard-home__banner-alert"
            />
          )}
          <div ref={dashboardContentRef} className="dashboard-home__content">
        {/* <Row gutter={[12, 0]} style={{ marginBottom: 24 }}>
          <Col flex={1}>
            <Card
              hoverable
              onClick={() => navigate('/accounts')}
              style={{
                width: "100%",
                height: "112px",
                borderRadius: "16px",
                padding: "20px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                border: "1px solid #f0f0f0",
                opacity: 1,
                cursor: "pointer",
              }}
              bodyStyle={{
                padding: "0",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Statistic
                title={<span style={{ fontSize: '13px' }}>Total AWS Accounts</span>}
                value={accounts}
                valueStyle={{ fontSize: '20px' }}
              />
            </Card>
          </Col>
          <Col flex={1}>
            <Card
              hoverable
              onClick={() => navigate('/accounts?tab=databricks')}
              style={{
                width: "100%",
                height: "112px",
                borderRadius: "16px",
                padding: "20px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                border: "1px solid #f0f0f0",
                opacity: 1,
                cursor: "pointer",
              }}
              bodyStyle={{
                padding: "0",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Statistic
                title={<span style={{ fontSize: '13px' }}>Databricks Accounts</span>}
                value={databricksAccountsCount}
                valueStyle={{ fontSize: '20px' }}
              />
            </Card>
          </Col>
          <Col flex={1}>
            <Card
              hoverable
              style={{
                width: "100%",
                height: "112px",
                borderRadius: "16px",
                padding: "20px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                border: "1px solid #f0f0f0",
                opacity: 1,
              }}
              bodyStyle={{
                padding: "0",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Statistic
                title={<span style={{ fontSize: '13px' }}>Active Regions</span>}
                value={regions}
                valueStyle={{ fontSize: '20px' }}
              />
            </Card>
          </Col>
          <Col flex={1}>
            <Card
              hoverable
              onClick={() => navigate('/metrics')}
              style={{
                width: "100%",
                height: "112px",
                borderRadius: "16px",
                padding: "20px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                border: "1px solid #f0f0f0",
                opacity: 1,
                cursor: "pointer",
              }}
              bodyStyle={{
                padding: "0",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Statistic
                title={<span style={{ fontSize: '13px' }}>Total Pipelines</span>}
                value={jobs}
                valueStyle={{ fontSize: '20px' }}
              />
            </Card>
          </Col>
          <Col flex={1}>
            <Card
              hoverable
              onClick={() => navigate('/logs')}
              style={{
                width: "100%",
                height: "112px",
                borderRadius: "16px",
                padding: "20px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                border: "1px solid #f0f0f0",
                opacity: 1,
                cursor: "pointer",
              }}
              bodyStyle={{
                padding: "0",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Statistic
                title={<span style={{ fontSize: '13px' }}>Log Count</span>}
                value={totalLogCount || log_groups}
                valueStyle={{ fontSize: '20px' }}
              />
            </Card>
          </Col>
        </Row> */}
            <Row
              gutter={[12, 12]}
              className={`dashboard-home__kpi-row ${
                dashboardMode === "normal" ? "dashboard-home__kpi-row--normal" : ""
              }`}
            >
              <Col {...normalKpiColProps}>
                <Card
                  hoverable
                  onClick={() => navigate("/accounts")}
                  className="dashboard-home__kpi-card"
                  data-kpi="aws-accounts"
                >
                  <div className="dashboard-home__kpi-value-wrap">
                    <div className="dashboard-home__kpi-icon-wrap">
                      {kpiBrandIcon(
                        awsLogoIcon,
                        "AWS",
                        "dashboard-home__kpi-brand-icon dashboard-home__kpi-brand-icon--aws"
                      )}
                    </div>
                    {dashboardLoading ? (
                      kpiValueSkeleton
                    ) : (
                      <span className="dashboard-home__kpi-value">
                        {accounts}
                      </span>
                    )}
                  </div>
                  <div className="dashboard-home__kpi-label">
                    AWS Accounts
                  </div>
                  <div className="dashboard-home__kpi-context">
                    Configured
                  </div>
                </Card>
              </Col>
              <Col {...normalKpiColProps}>
                <Card
                  hoverable
                  onClick={() => navigate("/accounts?tab=databricks")}
                  className="dashboard-home__kpi-card"
                  data-kpi="databricks"
                >
                  <div className="dashboard-home__kpi-value-wrap">
                    <div className="dashboard-home__kpi-icon-wrap">
                      {kpiBrandIcon(
                        databricksLogoIcon,
                        "Databricks",
                        "dashboard-home__kpi-brand-icon dashboard-home__kpi-brand-icon--databricks"
                      )}
                    </div>
                    {databricksLoading ? (
                      kpiValueSkeleton
                    ) : (
                      <span className="dashboard-home__kpi-value">
                        {databricksAccountsCount}
                      </span>
                    )}
                  </div>
                  <div className="dashboard-home__kpi-label">
                    Databricks Accounts
                  </div>
                  <div className="dashboard-home__kpi-context">
                    Connected
                  </div>
                </Card>
              </Col>
              <Col {...normalKpiColProps}>
                <Card hoverable className="dashboard-home__kpi-card" data-kpi="regions">
                  <div className="dashboard-home__kpi-value-wrap">
                    <div className="dashboard-home__kpi-icon-wrap">
                      {kpiBrandIcon(
                        regionsLogoIcon,
                        "Regions",
                        "dashboard-home__kpi-brand-icon dashboard-home__kpi-brand-icon--regions"
                      )}
                    </div>
                    {dashboardLoading ? (
                      kpiValueSkeleton
                    ) : (
                      <span className="dashboard-home__kpi-value">
                        {regions}
                      </span>
                    )}
                  </div>
                  <div className="dashboard-home__kpi-label">
                    Active Regions
                  </div>
                  <div className="dashboard-home__kpi-context">
                    Across regions
                  </div>
                </Card>
              </Col>
              <Col {...normalKpiColProps}>
                <Card
                  hoverable
                  onClick={() => {
                    const aid = pipelineCountAccountId || logAccountOptions.find((o) => o.value !== "__all__")?.value;
                    if (aid) {
                      navigate(`/pipeline-discovery?accountId=${aid}`);
                    } else {
                      navigate("/pipeline-discovery");
                    }
                  }}
                  className="dashboard-home__kpi-card"
                  data-kpi="resources"
                >
                  <div className="dashboard-home__kpi-value-wrap">
                    <div className="dashboard-home__kpi-icon-wrap">
                      <DeploymentUnitOutlined />
                    </div>
                    {pipelineCountLoading && pipelineCount == null ? (
                      kpiValueSkeleton
                    ) : (
                      <span className="dashboard-home__kpi-value">
                        {pipelineCount ?? 0}
                      </span>
                    )}
                  </div>
                  <div className="dashboard-home__kpi-label">
                    Total Pipelines
                  </div>
                  <div className="dashboard-home__kpi-context">
                    {pipelineCountRefreshing
                      ? "Updating from discovery..."
                      : "Discovery total"}
                  </div>
                </Card>
              </Col>
              <Col {...normalKpiColProps}>
                <Card
                  hoverable
                  onClick={() => navigate("/logs")}
                  className="dashboard-home__kpi-card"
                  data-kpi="logs"
                >
                  <div className="dashboard-home__kpi-value-wrap">
                    <div className="dashboard-home__kpi-icon-wrap">
                      <FileTextOutlined />
                    </div>
                    {totalLogCountLoading && totalLogCount == null && dashboardLoading ? (
                      kpiValueSkeleton
                    ) : (
                      <span className="dashboard-home__kpi-value">
                        {totalLogCount ?? log_groups}
                      </span>
                    )}
                  </div>
                  <div className="dashboard-home__kpi-label">
                    Log sources
                  </div>
                  <div className="dashboard-home__kpi-context">
                    {totalLogCountLoading && (totalLogCount ?? log_groups) != null
                      ? "Onboarded (updating...)"
                      : "Onboarded"}
                  </div>
                </Card>
              </Col>
              <Col {...normalKpiColProps}>
                <Tooltip title={healthTooltipTitle} placement="bottomLeft">
                  <Card
                    hoverable={false}
                    className="dashboard-home__kpi-card dashboard-home__kpi-card--static"
                    data-kpi={healthStatus?.status === 'unhealthy' ? 'health-unhealthy' : 'health'}
                  >
                    <div className="dashboard-home__kpi-value-wrap">
                      <div className="dashboard-home__kpi-icon-wrap">
                        <HeartOutlined />
                      </div>
                      {!healthStatus ? (
                        kpiValueSkeleton
                      ) : (
                        <span
                          className="dashboard-home__kpi-value dashboard-home__kpi-value--status"
                          style={{ color: getHealthColor() }}
                        >
                          {healthKpiShort}
                        </span>
                      )}
                    </div>
                    <div className="dashboard-home__kpi-label">
                      System health
                    </div>
                    <div className="dashboard-home__kpi-context">
                      Platform status
                    </div>
                  </Card>
                </Tooltip>
              </Col>
            </Row>
      {/* <Row gutter={[16, 16]} style={{ marginTop: 24, alignItems: 'stretch' }}>
        <Col xs={24}>
          <Card
            title="DATA USAGE OVERVIEW"            
            style={{
              width: '100%',
              gap: 16,
              opacity: 1,
              padding: 24,
              borderRadius: 16,
              marginTop: 24,
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              border: "1px solid #f0f0f0",
              background: "#fff",
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CostBarChart />
          </Card>
        </Col>
        <Col xs={24} md={12} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <Card
            title="Firing Alerts"
            style={{
              minWidth: 462,
              gap: 16,
              opacity: 1,
              padding: 24,
              borderRadius: 16,
              marginTop: 24,
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              border: "1px solid #f0f0f0",
              background: "#fff",
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <FiringAlertsList />
          </Card>
        </Col>
        <Col xs={24} md={12} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <Card
            title="Log Service Volume Distribution (Last 24 Hours)"
            style={{
              minWidth: 462,
              gap: 16,
              opacity: 1,
              padding: 24,
              borderRadius: 16,
              marginTop: 24,
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              border: "1px solid #f0f0f0",
              background: "#fff",
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {filteredServicePieData && filteredServicePieData.length > 0 && hasNonZeroData ? (
              <DonutChartWithRightLabels data={filteredServicePieData} />
            ) : filteredServicePieData && filteredServicePieData.length > 0 && !hasNonZeroData ? (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                minHeight: '200px',
                color: '#888'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
                <div style={{ fontSize: '16px', fontWeight: 500, marginBottom: '8px' }}>No Log Data</div>
                <div style={{ fontSize: '14px', textAlign: 'center' }}>No logs have been ingested in the last 6 hours</div>
              </div>
            ) : (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                minHeight: '200px',
                color: '#888'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
                <div style={{ fontSize: '16px', fontWeight: 500, marginBottom: '8px' }}>No Data Available</div>
                <div style={{ fontSize: '14px', textAlign: 'center' }}>Configure log sources to see volume distribution</div>
              </div>
            )}
          </Card>
        </Col>

      </Row> */}
            {/* Main grid: row of 3 (services, pipelines, firing alerts) + row of 2 (usage, logs) */}
            <div
              className={`dashboard-home__insight-grid ${
                dashboardMode === "normal"
                  ? "dashboard-home__insight-grid--collapsed"
                  : "dashboard-home__insight-grid--expanded"
              }`}
              aria-hidden={dashboardMode === "normal"}
            >
              {dashboardMode === "detailed" && insightsReady && (
                <>
              <Row gutter={[16, 16]} className="dashboard-home__section-row dashboard-home__insight-row-3" align="stretch" wrap>
                <Col xs={24} lg={8} className="dashboard-home__insight-col">
                  <Card
                    title={
                      <span className="dashboard-home__card-title">
                        Top services by volume
                        <span className="dashboard-home__card-title-meta">Last 24h</span>
                      </span>
                    }
                    className="dashboard-home__section-card dashboard-home__section-card--insight"
                    style={{ width: "100%" }}
                  >
                    <div className="dashboard-home__field">
                      <div className="dashboard-home__field-label">
                        Account
                      </div>
                      <Select
                        value={selectedLogAccount || undefined}
                        onChange={setSelectedLogAccount}
                        style={{ width: "100%" }}
                        placeholder="Select account"
                        size="middle"
                        showSearch
                        optionFilterProp="label"
                        options={logAccountOptions}
                        loading={!logAccountOptions.length}
                        popupClassName="dashboard-home__select-dropdown"
                      />
                    </div>
                    {servicePieLoading ? (
                      <div className="dashboard-home__chart-skeleton" aria-busy="true" aria-label="Loading chart">
                        <Skeleton active title={false} paragraph={{ rows: 5, width: ['100%', '92%', '88%', '95%', '70%'] }} />
                      </div>
                    ) : (
                      <TopServicesHorizontalBars data={filteredServicePieData} />
                    )}
                  </Card>
                </Col>
                <Col xs={24} lg={8} className="dashboard-home__insight-col">
                  <Card
                    title={
                      <span className="dashboard-home__card-title">
                        Service inventory
                        <span className="dashboard-home__card-title-meta">Platform registry</span>
                      </span>
                    }
                    className="dashboard-home__section-card dashboard-home__section-card--insight"
                    style={{ width: "100%" }}
                  >
                    <ResourceTrendChart />
                  </Card>
                </Col>
                <Col xs={24} lg={8} className="dashboard-home__insight-col">
                  <Card
                    title={<span className="dashboard-home__card-title">Firing alerts</span>}
                    className="dashboard-home__section-card dashboard-home__section-card--insight"
                    style={{ width: "100%" }}
                  >
                    <FiringAlertsList
                      scopeMonitored={{ accounts, regions }}
                      layout="grid"
                    />
                  </Card>
                </Col>
              </Row>
              <Row gutter={[16, 16]} className="dashboard-home__section-row dashboard-home__insight-row-2" align="stretch" wrap>
                <Col xs={24} lg={12} className="dashboard-home__insight-col">
                  <Card
                    title={<span className="dashboard-home__card-title">Data usage overview</span>}
                    className="dashboard-home__section-card dashboard-home__section-card--insight"
                    style={{ width: "100%" }}
                  >
                    <DataUsageChart />
                  </Card>
                </Col>
                <Col xs={24} lg={12} className="dashboard-home__insight-col">
                  <Card
                    title={<span className="dashboard-home__card-title">Logs processed timeline</span>}
                    className="dashboard-home__section-card dashboard-home__section-card--insight"
                    style={{ width: "100%" }}
                  >
                    <LogVolumeTimeline accountId={selectedLogAccount} />
                  </Card>
                </Col>
              </Row>
                </>
              )}
            </div>
          </div>
        </div>
    </div>
  );
};

export default DashboardPage;
